import { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Avatar } from "../../components/Avatar/Avatar";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Pill } from "../../components/Pill/Pill";
import { SegmentedToggle } from "../../components/SegmentedToggle/SegmentedToggle";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { Table, type Column } from "../../components/Table/Table";

import { formatWhen } from "./formatTime";
import type { GiftDirection, PlayerGift, PlayerGiftsResponse } from "./types";
import styles from "./PlayerGiftsCard.module.css";

// === Formatting helpers ============================================== //

/**
 * The channel as it is read beside a name: "· friend", not "· friends". The
 * config spells the allow-list in the plural because it is a list of routes;
 * one row went through exactly one of them.
 *
 * Anything this build has not heard of is printed as it came — the allow-list
 * lives in the project's config, and a channel added there is not a reason for
 * a row to say nothing.
 */
function channelLabel(channel: string): string {
  if (channel === "friends") return "friend";
  return channel;
}

// === Component ====================================================== //

interface Props {
  projectId: string;
  /** Which direction the tabs are on. Owned by the page — it keys the query. */
  direction: GiftDirection;
  onDirectionChange: (next: GiftDirection) => void;
  /** The answer for `direction`, or undefined while the first one is in flight. */
  data: PlayerGiftsResponse | undefined;
  loading: boolean;
  failed: boolean;
  /**
   * True while the OTHER direction is being fetched and `data` is still the
   * previous answer. The four numbers survive it — the server sends the same
   * four with either direction — but the ROWS do not: sent gifts drawn under a
   * "From" header would be a lie with nothing on screen to give it away.
   */
  stale: boolean;
}

/**
 * The player's gifts: four figures, a Sent/Received switch and the last 20
 * rows of whichever side is showing.
 *
 * Read-only, and deliberately so. Cancelling or returning a gift is not an
 * operation the API has, and a button here for it would be a promise the
 * server never made.
 *
 * Like the Linked-accounts card, its query is outside the page's render gate,
 * so every state it can be in — loading, failed, empty — is drawn inside the
 * card body and the editor below stays usable.
 */
export function PlayerGiftsCard({
  projectId,
  direction,
  onDirectionChange,
  data,
  loading,
  failed,
  stale,
}: Props) {
  // The switch is drawn OUTSIDE every failure branch, and that is the point of
  // the split below: a 500 on «Received» used to replace the whole card body
  // with a note, leaving no control to go back with — and `retry: 1` plus
  // `refetchOnWindowFocus: false` means the card does not recover on its own
  // either. The operator had to reload the page to undo one click.
  //
  // The four figures cannot be drawn on a failure that returned nothing, since
  // they arrive in the same answer as the rows. They are the same four for
  // either direction, so a failed SWITCH loses them only until the tab that
  // works is picked again.
  let tableArea: ReactNode;
  if (failed && !data) {
    tableArea = (
      <div className={styles.note} role="status">
        Couldn&apos;t load gifts.
      </div>
    );
  } else if (loading || !data) {
    tableArea = <GiftRowsSkeleton />;
  } else {
    const columns: Column<PlayerGift>[] = [
      {
        key: "when",
        header: "When",
        width: "150px",
        cell: (g) => <span className={styles.when}>{formatWhen(g.created_at)}</span>,
      },
      {
        key: "type",
        header: "Type",
        width: "110px",
        // The project's own word for what was sent, quoted rather than
        // prettified: it is the string the game API takes.
        cell: (g) => <code className={styles.type}>{g.type}</code>,
      },
      {
        key: "amount",
        header: "Amount",
        width: "80px",
        cell: (g) => <span className={styles.amt}>{g.amount}</span>,
      },
      {
        key: "party",
        // One column, two questions: the row's other side is the recipient in
        // the sent list and the sender in the received one.
        header: direction === "sent" ? "To" : "From",
        cell: (g) => <Party projectId={projectId} gift={g} />,
      },
      {
        key: "status",
        header: "Status",
        width: "130px",
        cell: (g) => <StatusPill gift={g} />,
      },
    ];

    tableArea = stale ? (
      <GiftRowsSkeleton />
    ) : data.items.length === 0 ? (
      <EmptyState
        title={
          direction === "sent"
            ? "This player has sent no gifts"
            : "This player has received no gifts"
        }
      />
    ) : (
      <Table columns={columns} rows={data.items} />
    );
  }

  const summary = data?.summary;
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>Gifts</h3>
        <span className={styles.cardSub}>sent · received</span>
      </div>
      <div className={styles.cardBody}>
        {summary ? (
          <div className={styles.summary}>
            <div className={styles.sumCell}>
              <div className={styles.sumKey}>Sent · 30 days</div>
              <div className={styles.sumVal}>{summary.sent_30d}</div>
            </div>
            <div className={styles.sumCell}>
              <div className={styles.sumKey}>Received · 30 days</div>
              <div className={styles.sumVal}>{summary.received_30d}</div>
            </div>
            <div className={styles.sumCell}>
              <div className={styles.sumKey}>Unclaimed out</div>
              <div className={styles.sumVal}>
                {summary.unclaimed_out}{" "}
                <span className={styles.of}>in other inboxes</span>
              </div>
            </div>
            <div className={styles.sumCell}>
              <div className={styles.sumKey}>Unclaimed in</div>
              <div className={styles.sumVal}>
                {summary.unclaimed_in}{" "}
                <span className={styles.of}>waiting here</span>
              </div>
            </div>
          </div>
        ) : (
          loading && <GiftSummarySkeleton />
        )}

        <div className={styles.tabsRow}>
          <SegmentedToggle
            aria-label="Gift direction"
            value={direction}
            onChange={onDirectionChange}
            options={[
              { value: "sent", label: "Sent", count: summary?.sent_30d },
              {
                value: "received",
                label: "Received",
                count: summary?.received_30d,
              },
            ]}
          />
          {/* What the table is, said before it is read: there is no paging
              here, and a page-2 nobody offers is a page-2 nobody looks for. */}
          <span className={styles.tabsHint}>Last 20, newest first</span>
        </div>

        {tableArea}

        <div className={styles.cardFoot}>
          A gift is <b>Pending</b> until the recipient takes it out of their
          inbox, <b>Claimed</b> once nothing is left on it, and <b>Expired</b>{" "}
          once its deadline passed with something still on it — the same three
          states the inbox reads. A partly taken gift says how much of it is
          gone.
        </div>
      </div>
    </div>
  );
}

// === Cells =========================================================== //

/**
 * The other side of the gift: avatar, name, channel.
 *
 * The name links to that player's own card because the next step of any gift
 * question is there — without it the operator copies the name into the players
 * search and arrives at the same place two screens later.
 */
function Party({ projectId, gift }: { projectId: string; gift: PlayerGift }) {
  const party = gift.counterparty;
  return (
    <span className={styles.party}>
      {party.found ? (
        <>
          <Avatar
            name={party.display_name}
            image={party.avatar_url ?? undefined}
            size={22}
          />
          <Link to={`/projects/${projectId}/players/${party.id}`}>
            {party.display_name}
          </Link>
        </>
      ) : (
        // Deleted since. No name came with the row and there is no card to
        // open, so the cell says the one thing that is known — and draws no
        // avatar, which would be initials of a name nobody has.
        <span className={styles.gone}>deleted player</span>
      )}
      <span className={styles.via}>· {channelLabel(gift.channel)}</span>
    </span>
  );
}

/**
 * Pending / Claimed / Expired — and, for a gift still open that somebody took
 * half of, how much of it is gone.
 *
 * The STATUS decides first, and the partial figure only refines `pending`. An
 * expired gift that was half taken is the one row where the two readings
 * disagree, and `Expired` is the true one: its deadline passed with something
 * still on it, and that something BURNED. Printing «Claimed 1 of 2» there would
 * report a loss as a partial success — which is the opposite of what an
 * operator opens this card to find out.
 */
function StatusPill({ gift }: { gift: PlayerGift }) {
  if (gift.status === "claimed") {
    return (
      <Pill size="sm" variant="success">
        Claimed
      </Pill>
    );
  }
  if (gift.status === "expired") {
    return (
      <Pill size="sm" variant="warn">
        Expired
      </Pill>
    );
  }
  if (gift.claimed_amount > 0) {
    return (
      <Pill size="sm" variant="info">
        Claimed {gift.claimed_amount} of {gift.amount}
      </Pill>
    );
  }
  return (
    <Pill size="sm" variant="neutral">
      Pending
    </Pill>
  );
}

// === Loading skeletons =============================================== //

/**
 * The four figures' shape while the first answer is in flight.
 *
 * Only the figures: the switch beside them is real from the first paint, since
 * it is the one control that has to survive a failed answer — see the split in
 * the component above.
 */
function GiftSummarySkeleton() {
  return (
    <div className={styles.summary}>
      {[0, 1, 2, 3].map((i) => (
        <div className={styles.sumCell} key={i}>
          <Skeleton width={70} height={9} />
          <Skeleton width="55%" height={20} style={{ marginTop: 4 }} />
        </div>
      ))}
    </div>
  );
}

/**
 * Five placeholder rows — the handful a card usually holds, not the twenty it
 * may. A skeleton the height of the maximum would drop the rest of the page
 * out of sight for the time of one request and pull it back up when the answer
 * turns out to be three rows.
 */
function GiftRowsSkeleton() {
  return (
    <div className={styles.skelRows}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div className={styles.skelRow} key={i}>
          <Skeleton width={92} height={12} />
          <Skeleton width={54} height={12} />
          <Skeleton width={20} height={12} />
          <Skeleton width={130} height={12} />
          <Skeleton width={62} height={16} radius={999} />
        </div>
      ))}
    </div>
  );
}
